import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { premiumCardInsetSx } from "../../styles/Theme";
import { IconFilterSvg } from "../../common/utils/icons";
import useFetchResearchQueryRecordsByRuleResultId from "../../hooks/useFetchResearchQueryRecordsByRuleResultId";
import ResearchQueryResultsDetail from "./ResearchQueryResultsDetail";
import ResearchQueryResultsWithIssues from "./ResearchQueryResultsWithIssues";
import useFetchResearchIssues from "../../hooks/useFetchResearchIssues";
import {
  ClassificationColumn,
  CommentColumn,
  CurrentStatusColumn,
  ReasonColumn,
  UserColumn,
} from "../../common/utils/commonActionsFields";
import { IconIncidentDetails } from "../../common/utils/icons";
import { useAuth } from "../../contexts/AuthContext";

import {
  calculateDaysInQueue,
  getFormattedDateTime,
} from "../../services/utils";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";

interface IRootCauseAnalysisProps {
  selectedColumns: any;
  isLoading: boolean;
  heading: string;
  dashboardData: any;
}

const RootCauseAnalysis = ({
  selectedColumns,
  isLoading,
  heading,
  dashboardData,
}: IRootCauseAnalysisProps) => {
  const { hasPermission } = useAuth();
  const canEditRuleExecutionIssues = hasPermission(
    "rule_execution_issues",
    "edit",
  );

  const [rootCauseAnalysisData, setRootCauseAnalysisData] = useState<any>([]);
  const [resourceKeys, setResourceKeys] = useState<any>([]);
  const [isFilterHide, setIsFilterHide] = useState(true);
  const [isResearchQueryLoading, setIsResearchQueryLoading] =
    useState<boolean>(false);
  const [selectedQueryName, setSelectedQueryName] = useState<any>("");
  // Track if this is the initial page load
  //for pagination default values and states
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [pageN, setPageN] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pageS, setPageS] = useState<number>(rowsPerPage);

  const [columnsData, setColumnsData] = useState<any>([]);
  const [rowsData, setRowsData] = useState<any>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [dashboardUserDetailsData, setDashboardUserDetailsData] = useState<any>(
    {
      status: false,
      incidentId: null,
      assigned_user: null,
      panelType: "",
      isBackDropVisible: false,
      comment: [],
    },
  );
  const [userDialogData, setUserDialogData] = useState<any>({
    status: false,
    dialogName: "",
    selectedIds: [],
  });

  useEffect(() => {
    console.log("dashboardUserDetailsData", dashboardUserDetailsData);
  }, [dashboardUserDetailsData]);

  // Check if the selected query has an error in comparison_research_query_results
  const hasQueryError = () => {
    if (
      !dashboardData?.comparison_research_query_results
        ?.research_query_results ||
      !selectedQueryName
    ) {
      return false;
    }

    const researchQueries =
      dashboardData.comparison_research_query_results.research_query_results;
    if (!Array.isArray(researchQueries) || researchQueries.length === 0) {
      return false;
    }

    // Find the query object by name
    const queryObject = researchQueries.find(
      (q: any) => q.name === selectedQueryName,
    );
    if (
      !queryObject ||
      !queryObject.result ||
      !Array.isArray(queryObject.result)
    ) {
      return false;
    }

    // Check if the first item in the result array has an error property
    return queryObject.result[0]?.hasOwnProperty("error");
  };

  // Get the error message for the selected query
  const getQueryErrorMessage = () => {
    if (!hasQueryError()) {
      return null;
    }

    const researchQueries =
      dashboardData.comparison_research_query_results.research_query_results;
    const queryObject = researchQueries.find(
      (q: any) => q.name === selectedQueryName,
    );
    return queryObject?.result[0]?.error || "An unknown error occurred";
  };

  // Fetch research issues data - only enabled if there's no error
  const [researchIssuesData, refetchResearchIssues] = useFetchResearchIssues({
    executionId: dashboardData?.id || null,
    queryName: selectedQueryName,
    page: pageN,
    pageSize: pageS,
    setIsLoading: setIsResearchQueryLoading,
    enabled: !!dashboardData?.id && !!selectedQueryName && !hasQueryError(),
    isDetailedExecutionDataAvailable:
      dashboardData?.is_detailed_execution_data_available,
    isIssuesDataAvailable: dashboardData?.is_issues_data_available,
  });

  useEffect(() => {
    if (researchIssuesData?.items?.length > 0) {
      setRootCauseAnalysisData(researchIssuesData?.items);
    } else if (researchIssuesData?.items?.length === 0) {
      setRootCauseAnalysisData([]);
    }
  }, [researchIssuesData]);

  useEffect(() => {
    if (!dashboardData) return;
    if (
      dashboardData.comparison_research_query_results?.research_query_results &&
      dashboardData?.is_detailed_execution_data_available
    ) {
      setRootCauseAnalysisData(
        dashboardData.comparison_research_query_results.research_query_results,
      );
    }

    if (dashboardData.comparison_research_query_results?.resource_keys) {
      setResourceKeys(
        dashboardData.comparison_research_query_results.resource_keys,
      );
    }
    setSelectedQueryName(
      dashboardData?.comparison_research_query_results
        ?.research_query_results[0]?.name,
    );
  }, [dashboardData]);
  const handleFilterBox = () => {
    setIsFilterHide(!isFilterHide);
  };

  useEffect(() => {
    // Check if there's an error for the selected query
    if (hasQueryError()) {
      const errorMsg = getQueryErrorMessage();
      setErrorMessage(errorMsg);
      setColumnsData([]);
      setRowsData([]);
      return;
    }

    // Clear error message if no error
    setErrorMessage(null);

    if (researchIssuesData?.items?.length > 0) {
      console.log("researchIssuesData", researchIssuesData);
      // Create rows from research issues data
      const rows = researchIssuesData?.items.map((item: any, index: any) => {
        const researchResult =
          (dashboardData?.is_issues_data_available
            ? item?.comparison_research_record_details?.research_query_results
                ?.result
            : item?.research_query_results?.result) || {};

        // Calculate days in queue
        const diffInDays = calculateDaysInQueue(item.first_occurrence);

        return {
          id: index,
          rowId: item.id, // Use the actual issue ID for operations
          current_status: item.current_status,
          assigned_user: item.assigned_user,
          comment: item.comment,
          create_date: item.create_date,
          update_date: item.update_date,
          first_occurrence: item.first_occurrence,
          assigned_by: item.assigned_by,
          issue_type: item.issue_type,
          rule_execution_id: item.rule_execution_id,
          days_in_queue: diffInDays,
          issue_classification_id: item?.issue_classification_id,
          issue_reason_id: item?.issue_reason_id,
          ...researchResult, // Spread the research query result data
        };
      });
      console.log("rows", rows);

      // Create columns from the first research result object
      const firstResult = dashboardData?.is_issues_data_available
        ? researchIssuesData?.items[0]?.comparison_research_record_details
            ?.research_query_results?.result
        : researchIssuesData?.items[0]?.research_query_results?.result;
      let dynamicColumns: any[] = [];

      if (firstResult) {
        dynamicColumns = Object.keys(firstResult).map((key) => ({
          field: key,
          headerName: (
            <Tooltip title={`${key}`} placement={"top"} arrow>
              <span>{`${key.slice(0, 15)}${
                key.length >= 15 ? "..." : ""
              } `}</span>
            </Tooltip>
          ),
          width: 160,
        }));
      }

      // Add days in queue column
      const daysInQueueColumn = [
        {
          field: "days_in_queue",
          headerName: "Age",
          flex: 1,
          minWidth: 110,
          renderCell: (params: any) => {
            return (
              <Box>
                {params?.row?.first_occurrence === null ? (
                  (params.value ?? 0)
                ) : (
                  <Tooltip
                    title={
                      <Typography
                        sx={{ fontSize: "0.875rem" }}
                      >{`First Occurrence : ${getFormattedDateTime(
                        params?.row?.first_occurrence,
                      )}`}</Typography>
                    }
                    placement="top"
                    className="tooltip-ellipsis"
                  >
                    {params.value ?? 0}
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
      ];

      // Add issue management columns
      const issueColumns = [
        ...(dashboardData?.is_issues_data_available
          ? [
              CurrentStatusColumn({}),
              UserColumn({}),
              CommentColumn({}),
              ClassificationColumn({}),
              ReasonColumn({}),
              {
                field: "action",
                headerName: "Actions",
                align: "center",
                headerAlign: "center",
                minWidth: "130",
                pinned: "right",
                renderCell: (params: any) => {
                  return (
                    canEditRuleExecutionIssues && (
                      <Box sx={{ display: "inline-flex", columnGap: "12px" }}>
                        <Tooltip
                          title={`Issue Details : ${params?.row?.rowId}`}
                          placement="top"
                          arrow
                        >
                          <Button
                            className="btn-blue btn-orange btn-sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setDashboardUserDetailsData((prev: any) => ({
                                ...prev,
                                status: true,
                                incidentId: params.row.rowId,
                                assigned_user: params.row.assigned_user,
                                panelType: "incidentDetail",
                                isBackDropVisible: true,
                                first_occurrence: params.row.first_occurrence,
                                issue_classification_id:
                                  params.row.issue_classification_id,
                                issue_reason_id: params.row.issue_reason_id,
                                current_status: params.row.current_status,
                                comment: [
                                  {
                                    comment: params.row.comment,
                                    create_date: params.row.create_date,
                                  },
                                ],
                              }));
                            }}
                          >
                            <IconIncidentDetails />
                          </Button>
                        </Tooltip>
                      </Box>
                    )
                  );
                },
              },
              ...daysInQueueColumn,
            ]
          : []),
      ];

      // Combine all columns
      const allColumns = [...dynamicColumns, ...issueColumns];

      // Apply filtering if selectedColumns exist
      let filteredRows = rows;
      if (
        selectedColumns &&
        selectedColumns.length > 0 &&
        resourceKeys?.base_columns
      ) {
        const baseColumns = resourceKeys.base_columns;
        const valuesToMatch = new Set(
          selectedColumns
            .flatMap((item: { [x: string]: any }) =>
              baseColumns.map((col: string | number) => item[col]),
            )
            .filter((val: undefined) => val !== undefined),
        );

        filteredRows = rows.filter(
          (row: { [s: string]: unknown } | ArrayLike<unknown>) =>
            Object.entries(row).some(
              ([key, value]) =>
                key !== "id" &&
                key !== "rowId" &&
                valuesToMatch.has(String(value)),
            ),
        );
      }

      setColumnsData(allColumns);
      setRowsData(filteredRows);
    } else {
      setColumnsData([]);
      setRowsData([]);
    }
  }, [
    researchIssuesData,
    selectedColumns,
    resourceKeys,
    dashboardData,
    setDashboardUserDetailsData,
    setUserDialogData,
    selectedQueryName,
    dashboardData?.comparison_research_query_results?.research_query_results,
    dashboardData?.is_issues_data_available,
  ]);

  return (
    <Paper elevation={0} sx={premiumCardInsetSx}>
      <Accordion defaultExpanded className="heading-bold">
        <AccordionSummary
          aria-controls="research-content"
          id="research-header"
          expandIcon={<GridExpandMoreIcon />}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip
              title={`${isFilterHide ? "Show Filters" : "Hide Filters"} `}
              placement="top"
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterBox();
                }}
                onFocus={(e) => e.stopPropagation()}
              >
                <IconFilterSvg />
              </IconButton>
            </Tooltip>
            <h4 style={{ margin: 0 }}>{heading}</h4>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ paddingTop: "16px" }}>
          {dashboardData?.is_detailed_execution_data_available ? (
            <ResearchQueryResultsDetail
              rootCauseAnalysisData={rootCauseAnalysisData}
              selectedColumns={selectedColumns}
              isLoading={isLoading}
              isFilterHide={isFilterHide}
              resourceKeys={resourceKeys}
            />
          ) : (
            <ResearchQueryResultsWithIssues
              isLoading={isResearchQueryLoading}
              setIsLoading={setIsResearchQueryLoading}
              isFilterHide={isFilterHide}
              selectedQueryName={selectedQueryName}
              setSelectedQueryName={setSelectedQueryName}
              queryTabs={
                dashboardData?.comparison_research_query_results
                  ?.research_query_results
              }
              pageN={pageN}
              setPageN={setPageN}
              pageS={pageS}
              setPageS={setPageS}
              totalPages={researchIssuesData?.total}
              refetchResearchIssues={refetchResearchIssues}
              columnsData={columnsData}
              rowsData={rowsData}
              setDashboardUserDetailsData={setDashboardUserDetailsData}
              dashboardUserDetailsData={dashboardUserDetailsData}
              setUserDialogData={setUserDialogData}
              userDialogData={userDialogData}
              isIssuesDataAvailable={dashboardData?.is_issues_data_available}
              errorMessage={errorMessage}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default RootCauseAnalysis;
